import { getSupabaseClient } from '@/api/supabaseClient';

const STORAGE_BUCKET = import.meta.env.VITE_SUPABASE_STORAGE_BUCKET || 'media';

function todayIsoDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function handleResult(data, error, message) {
  if (error) throw new Error(`${message}: ${error.message}`);
  return data;
}

function buildUploadPath(file, folder) {
  const ext = file.name?.split('.').pop() || 'bin';
  const randomId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  return `${folder}/${Date.now()}_${randomId}.${ext}`;
}

function buildFileWithMimeType(file, mimeType) {
  if (typeof File !== 'undefined' && file instanceof File) {
    return new File([file], file.name, {
      type: mimeType,
      lastModified: file.lastModified,
    });
  }
  return new Blob([file], { type: mimeType });
}

function getUploadMimeType(file) {
  const extension = file?.name?.split('.').pop()?.toLowerCase() || '';
  const currentType = String(file?.type || '').toLowerCase();
  const mimeByExtension = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
  };

  if (!currentType || currentType === 'application/octet-stream') {
    return mimeByExtension[extension] || currentType;
  }

  // Some providers label MPEG audio as audio/mpeg or video/mpeg; remap before upload.
  if ((extension === 'mpeg' || extension === 'mpg') && /^(audio|video)\/mpeg$/i.test(currentType)) {
    return 'audio/mp4';
  }

  return currentType;
}

export async function uploadImage(file, folder = 'uploads') {
  const supabase = getSupabaseClient();
  const uploadPath = buildUploadPath(file, folder);
  const uploadMimeType = getUploadMimeType(file);
  const fileToUpload = uploadMimeType && uploadMimeType !== file?.type
    ? buildFileWithMimeType(file, uploadMimeType)
    : file;

  let { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(uploadPath, fileToUpload, { cacheControl: '31536000', upsert: false });

  if (uploadError) {
    throw new Error(`Erro no upload: ${uploadError.message}`);
  }

  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(uploadPath);
  return data.publicUrl;
}

export async function listAdminVideos(limit = 100) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('videos')
    .select('*')
    .order('created_date', { ascending: false })
    .limit(limit);
  return handleResult(data, error, 'Erro ao listar videos');
}

export async function listPublicVideos(limit = 50) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('videos')
    .select('*')
    .eq('is_active', true)
    .order('created_date', { ascending: false })
    .limit(limit);
  return handleResult(data, error, 'Erro ao listar videos');
}

export async function createVideo(payload) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('videos')
    .insert(payload)
    .select('*')
    .single();
  return handleResult(data, error, 'Erro ao criar video');
}

export async function updateVideo(id, payload) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('videos')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();
  return handleResult(data, error, 'Erro ao atualizar video');
}

export async function deleteVideo(id) {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from('videos').delete().eq('id', id);
  if (error) throw new Error(`Erro ao excluir video: ${error.message}`);
}

export async function listAdminAlbums(limit = 100) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('albums')
    .select('*')
    .order('created_date', { ascending: false })
    .limit(limit);
  return handleResult(data, error, 'Erro ao listar albuns');
}

export async function listPublicAlbums(limit = 50) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('albums')
    .select('*')
    .eq('is_active', true)
    .order('created_date', { ascending: false })
    .limit(limit);
  return handleResult(data, error, 'Erro ao listar albuns');
}

export async function createAlbum(payload) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('albums')
    .insert(payload)
    .select('*')
    .single();
  return handleResult(data, error, 'Erro ao criar album');
}

export async function updateAlbum(id, payload) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('albums')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();
  return handleResult(data, error, 'Erro ao atualizar album');
}

export async function deleteAlbum(id) {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from('albums').delete().eq('id', id);
  if (error) throw new Error(`Erro ao excluir album: ${error.message}`);
}

export async function listAdminBanners(limit = 50) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('banners')
    .select('*')
    .order('order', { ascending: true })
    .limit(limit);
  return handleResult(data, error, 'Erro ao listar banners');
}

export async function listPublicBanners(limit = 50) {
  const supabase = getSupabaseClient();
  const today = todayIsoDate();
  const { data, error } = await supabase
    .from('banners')
    .select('*')
    .eq('is_active', true)
    .order('order', { ascending: true })
    .limit(limit);
  const rows = handleResult(data, error, 'Erro ao listar banners');
  return (rows || []).filter((banner) => {
    if (!banner.expiration_date) return true;
    return String(banner.expiration_date) >= today;
  });
}

export async function createBanner(payload) {
  const supabase = getSupabaseClient();
  const normalizedPayload = {
    ...payload,
    expiration_date: payload?.expiration_date || null,
    signup_link: payload?.signup_link || null,
    signup_enabled: Boolean(payload?.signup_enabled),
    order: Number.isFinite(Number(payload?.order)) ? Number(payload.order) : 0,
  };
  const { data, error } = await supabase
    .from('banners')
    .insert(normalizedPayload)
    .select('*')
    .single();
  return handleResult(data, error, 'Erro ao criar banner');
}

export async function updateBanner(id, payload) {
  const supabase = getSupabaseClient();
  const normalizedPayload = { ...payload };
  if (Object.prototype.hasOwnProperty.call(payload || {}, 'expiration_date')) {
    normalizedPayload.expiration_date = payload?.expiration_date || null;
  }
  if (Object.prototype.hasOwnProperty.call(payload || {}, 'signup_link')) {
    normalizedPayload.signup_link = payload?.signup_link || null;
  }
  if (Object.prototype.hasOwnProperty.call(payload || {}, 'signup_enabled')) {
    normalizedPayload.signup_enabled = Boolean(payload?.signup_enabled);
  }
  if (Object.prototype.hasOwnProperty.call(payload || {}, 'order')) {
    normalizedPayload.order = Number.isFinite(Number(payload?.order)) ? Number(payload.order) : 0;
  }
  const { data, error } = await supabase
    .from('banners')
    .update(normalizedPayload)
    .eq('id', id)
    .select('*')
    .single();
  return handleResult(data, error, 'Erro ao atualizar banner');
}

export async function deleteBanner(id) {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from('banners').delete().eq('id', id);
  if (error) throw new Error(`Erro ao excluir banner: ${error.message}`);
}
